import Link from 'next/link';
import EmptyLayout from '@/components/layouts/empty-layout';
import LoginForm from '@/components/pages/auth/login-form/login-form';
import GoogleSignInButton from '@/components/google-signin-button';
import {FiArrowLeft} from 'react-icons/fi';
import {Component} from 'react';
import type {WrappedComponentProps as IntlProps} from 'react-intl';
import {FormattedMessage, injectIntl} from 'react-intl';
import {motion} from 'framer-motion';
import type {WithAuthProps} from '@/contexts/auth-context';
import {withAuth} from '@/contexts/auth-context';

interface State {
  readonly formVisible: boolean;
  readonly error: string;
}

class LoginPage extends Component<IntlProps & WithAuthProps, State> {
  public readonly state: State = {formVisible: true, error: ''};

  public render(): JSX.Element {
    const {formatMessage: t} = this.props.intl;
    const {formVisible, error} = this.state;
    return (
      <EmptyLayout metadata={{title: t({id: 'pages.login.page_title'})}}>
        <div className="flex items-stretch justify-center h-screen container-fluid">
          <div className="flex flex-col w-full lg:w-1/2">
            <div className="flex flex-1 items-center justify-center">
              <div className="text-center text-sm md:w-5/12">
                <img src="assets/images/soubul-192.png" alt="Soubul" className="block mx-auto w-16" />
                <h1 className="font-bold text-3xl my-3 text-gray-900">
                  <FormattedMessage id="words.welcome_to_soubul" />
                </h1>

                <div>
                  <FormattedMessage id="pages.login.do_not_have_an_account" />
                  <span>&nbsp;</span>
                  <Link href="/sign-up" passHref>
                    <a className="text-blue-500">
                      <FormattedMessage id="pages.login.sign_up_for_free" />
                    </a>
                  </Link>
                </div>

                <GoogleSignInButton />

                <span className="block text-red-500 text-sm mb-2">{error}</span>

                {formVisible ? (
                  <LoginForm />
                ) : (
                  <button className="text-blue-500" onClick={this.toggleLoginFormVisibility}>
                    <FormattedMessage id="pages.login.sign_in_with_email_or_phone_number" />
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center justify-center pb-10">
              <Link href="/" passHref>
                <a className="text-light-400 flex items-center">
                  <FiArrowLeft className="mx-1" />
                  <span className="mx-1">
                    <FormattedMessage id="words.back_to_the_home_page" />
                  </span>
                </a>
              </Link>
            </div>
          </div>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{hidden: {x: '100%', scale: 0}, visible: {x: 0, scale: 1}}}
            transition={{duration: 0.7, delay: 0.2}}
            className="hidden lg:block lg:w-1/2 bg-no-repeat bg-center bg-contain"
            style={{backgroundImage: 'url(assets/images/ills/ill_login_page.svg)'}}
          />
        </div>
      </EmptyLayout>
    );
  }

  /** @internal */
  private toggleLoginFormVisibility = () => {
    this.setState({formVisible: !this.state.formVisible, error: ''});
  };
}

export default withAuth(injectIntl(LoginPage));
